import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GamePhase.css';
import WerewolfChat from './WerewolfChat';

const PHASE_MESSAGES = {
  'werewolf': {
    title: '🌙 Đêm - Ma Sói',
    description: 'Màn đêm buông xuống, tất cả ngủ đi. Sói ơi dậy đi, hãy chọn nạn nhân của đêm nay.',
    action: 'werewolf'
  },
  'doctor': {
    title: '🌙 Đêm - Bảo Vệ',
    description: 'Bảo Vệ ơi dậy đi, hãy chọn một người để cứu.',
    action: 'doctor'
  },
  'seer': {
    title: '🌙 Đêm - Tiên Tri',
    description: 'Tiên Tri ơi dậy đi, hãy chọn một người mà tiên tri muốn soi xem có phải là sói không.',
    action: 'seer'
  },
  'witch-save': {
    title: '🌙 Đêm - Phù Thuỷ (Cứu)',
    description: 'Phù Thuỷ ơi dậy đi, đêm nay người này bị giết, phù thuỷ có muốn cứu không?',
    action: 'witch-save'
  },
  'witch-kill': {
    title: '🌙 Đêm - Phù Thuỷ (Giết)',
    description: 'Đêm nay phù thuỷ có muốn giết ai không?',
    action: 'witch-kill'
  },
  'hunter': {
    title: gameState => gameState.gameState === 'night' ? '🌙 Đêm - Thợ săn' : '☀️ Ngày - Thợ săn',
    description: gameState => gameState.gameState === 'night' 
      ? 'Hãy chọn 1 người chơi để nhắm. Nếu bạn bị sói giết đêm nay, người đó sẽ chết theo.'
      : 'Vì thợ săn đã bị giết, hãy chọn 1 người chơi chết cùng',
    action: gameState => gameState.gameState === 'night' ? 'hunter-aim' : 'hunter'
  },
  'voting-accuse': {
    title: '☀️ Ngày - Bỏ Phiếu Nghi Ngờ',
    description: 'Hãy bỏ phiếu cho người mà bạn nghĩ là ma sói.',
    action: 'vote'
  },
  'defense': {
    title: '☀️ Ngày - Bảo Vệ Bản Thân',
    description: 'Bạn đang bị nghi ngờ là ma sói, hãy thuyết phục người chơi khác tin bạn không phải.',
    action: null
  },
  'voting-decision': {
    title: '☀️ Ngày - Quyết Định',
    description: 'Hãy quyết định: Giết người bị nghi ngờ hay đổi ý?',
    action: 'vote'
  }
};

function GamePhase({ gameState, onAction, onVote, onSkipPhase, voteTieMessage, werewolfChatMessages = [], onWerewolfChat }) {
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [selectedVoteTarget, setSelectedVoteTarget] = useState(null);
  const [seerSubmitted, setSeerSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastPhase, setLastPhase] = useState(null);
  const [defenseTimer, setDefenseTimer] = useState(30);
  const defenseTimerIntervalRef = useRef(null);
  const onSkipPhaseRef = useRef(onSkipPhase);
  
  // Helper function to get defense phase start time from localStorage
  // Uses accusedPlayer ID to ensure we're using the correct timer for the current defense phase
  const getDefensePhaseStartTime = useCallback(() => {
    const accusedPlayerId = gameState.accusedPlayer;
    if (!accusedPlayerId) return null;
    
    const key = `defensePhaseStartTime_${accusedPlayerId}`;
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    // Verify this is still the same defense phase (same accused player)
    if (data.accusedPlayerId !== accusedPlayerId) {
      localStorage.removeItem(key);
      return null;
    }
    
    return data.startTime;
  }, [gameState.accusedPlayer]);
  
  // Helper function to store defense phase start time in localStorage
  const setDefensePhaseStartTime = useCallback((startTime) => {
    const accusedPlayerId = gameState.accusedPlayer;
    if (!accusedPlayerId) return;
    
    const key = `defensePhaseStartTime_${accusedPlayerId}`;
    localStorage.setItem(key, JSON.stringify({
      startTime,
      accusedPlayerId
    }));
  }, [gameState.accusedPlayer]);
  
  // Helper function to remove defense phase start time from localStorage
  const clearDefensePhaseStartTime = useCallback(() => {
    const accusedPlayerId = gameState.accusedPlayer;
    if (accusedPlayerId) {
      const key = `defensePhaseStartTime_${accusedPlayerId}`;
      localStorage.removeItem(key);
    }
    // Also clear any old keys (cleanup)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('defensePhaseStartTime_')) {
        localStorage.removeItem(key);
      }
    });
  }, [gameState.accusedPlayer]);
  
  // Helper function to calculate remaining time based on real time
  const calculateRemainingTime = (startTime) => {
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, 30000 - elapsed); // 30 seconds = 30000ms
    return Math.ceil(remaining / 1000); // Convert to seconds
  };
  
  // Reset seerSubmitted when phase changes
  useEffect(() => {
    if (gameState.phase !== 'seer') {
      setSeerSubmitted(false);
    }
    // If seer has submitted action, mark as submitted
    if (gameState.phase === 'seer' && gameState.nightActions?.seer && !seerSubmitted) {
      setSeerSubmitted(true);
      setSelectedTarget(gameState.nightActions.seer);
    }
    // Reset selectedTarget when phase changes to a new action phase (but preserve for seer)
    if (gameState.phase !== lastPhase && lastPhase !== null) {
      if (gameState.phase !== 'seer') {
        // Reset for all phases except seer (seer has special handling above)
        setSelectedTarget(null);
      }
    }
    // Clear loading when seer result is received (after initial submission)
    if (gameState.phase === 'seer' && gameState.seerResult !== undefined && seerSubmitted) {
      setIsLoading(false);
    }
    // Clear loading when vote is successfully recorded
    if ((gameState.phase === 'voting-accuse' || gameState.phase === 'voting-decision') && 
        ((gameState.phase === 'voting-accuse' && gameState.votes && gameState.votes[gameState.myId]) ||
         (gameState.phase === 'voting-decision' && gameState.decisionVotes && gameState.decisionVotes[gameState.myId]))) {
      setIsLoading(false);
      setSelectedVoteTarget(null);
    }
    // Reset vote selection when phase changes
    if (gameState.phase !== 'voting-accuse' && gameState.phase !== 'voting-decision') {
      setSelectedVoteTarget(null);
    }
    // Clear selection if it's the player themselves
    if ((gameState.phase === 'voting-accuse' || gameState.phase === 'voting-decision') && selectedVoteTarget === gameState.myId) {
      setSelectedVoteTarget(null);
    }
    // Clear loading when phase changes
    if (lastPhase !== null && gameState.phase !== lastPhase) {
      setIsLoading(false);
    }
    // Clear loading when game finishes
    if (gameState.gameState === 'finished') {
      setIsLoading(false);
    }
    // Clear loading when player becomes dead
    const currentPlayer = gameState.players?.find(p => p.id === gameState.myId);
    if (currentPlayer && !currentPlayer.isAlive) {
      setIsLoading(false);
    }
    setLastPhase(gameState.phase);
  }, [gameState.phase, gameState.nightActions?.seer, gameState.seerResult, gameState.votes, gameState.decisionVotes, gameState.werewolfVotes, gameState.myId, gameState.gameState, gameState.players, selectedVoteTarget, lastPhase, seerSubmitted]);

  // Keep onSkipPhase ref up to date
  useEffect(() => {
    onSkipPhaseRef.current = onSkipPhase;
  }, [onSkipPhase]);

  // Defense phase timer - 30 seconds auto-skip based on real time
  useEffect(() => {
    // Clear any existing timer first
    if (defenseTimerIntervalRef.current) {
      clearInterval(defenseTimerIntervalRef.current);
      defenseTimerIntervalRef.current = null;
    }

    // Check if entering defense phase
    if (gameState.phase === 'defense') {
      let startTime = getDefensePhaseStartTime();
      
      // Only create new start time if:
      // 1. No stored start time exists, OR
      // 2. Phase just changed TO defense (was not defense before, and lastPhase is not null)
      // This prevents resetting on page refresh when still in defense phase
      // On page refresh, lastPhase will be null, but if startTime exists, we use it
      const isPhaseTransition = lastPhase !== null && lastPhase !== 'defense';
      if (!startTime || isPhaseTransition) {
        startTime = Date.now();
        setDefensePhaseStartTime(startTime);
      }
      
      // Calculate initial remaining time
      const initialRemaining = calculateRemainingTime(startTime);
      setDefenseTimer(initialRemaining);
      
      // If timer already expired, skip immediately
      if (initialRemaining <= 0) {
        clearDefensePhaseStartTime();
        if (onSkipPhaseRef.current) {
          onSkipPhaseRef.current();
        }
        return;
      }
      
      // Start countdown timer that updates based on real time
      defenseTimerIntervalRef.current = setInterval(() => {
        const currentStartTime = getDefensePhaseStartTime();
        if (!currentStartTime) {
          // Start time was cleared (phase ended externally)
          if (defenseTimerIntervalRef.current) {
            clearInterval(defenseTimerIntervalRef.current);
            defenseTimerIntervalRef.current = null;
          }
          setDefenseTimer(30);
          return;
        }
        
        const remaining = calculateRemainingTime(currentStartTime);
        setDefenseTimer(remaining);
        
        if (remaining <= 0) {
          // Timer reached 0, auto-skip phase
          clearDefensePhaseStartTime();
          if (defenseTimerIntervalRef.current) {
            clearInterval(defenseTimerIntervalRef.current);
            defenseTimerIntervalRef.current = null;
          }
          if (onSkipPhaseRef.current) {
            onSkipPhaseRef.current();
          }
        }
      }, 100); // Update every 100ms for smoother countdown
    } else {
      // Not in defense phase - clear stored start time and reset timer
      // Only clear if we're actually leaving defense phase (not on initial mount)
      if (lastPhase === 'defense') {
        clearDefensePhaseStartTime();
      }
      setDefenseTimer(30);
    }

    // Cleanup interval when phase changes or component unmounts
    return () => {
      if (defenseTimerIntervalRef.current) {
        clearInterval(defenseTimerIntervalRef.current);
        defenseTimerIntervalRef.current = null;
      }
    };
  }, [gameState.phase, lastPhase, getDefensePhaseStartTime, setDefensePhaseStartTime, clearDefensePhaseStartTime]);


  const phase = gameState.phase;
  const rawPhaseInfo = PHASE_MESSAGES[phase] || { title: phase, description: '', action: null };
  // Handle phase info that might be functions (for dynamic content like hunter phase)
  const phaseInfo = {
    title: typeof rawPhaseInfo.title === 'function' ? rawPhaseInfo.title(gameState) : rawPhaseInfo.title,
    description: typeof rawPhaseInfo.description === 'function' ? rawPhaseInfo.description(gameState) : rawPhaseInfo.description,
    action: typeof rawPhaseInfo.action === 'function' ? rawPhaseInfo.action(gameState) : rawPhaseInfo.action
  };

  // Debug logging for voting-accuse phase
  useEffect(() => {
    if (phase === 'voting-accuse') {
      console.log('[GamePhase] Voting-accuse phase detected:', {
        phase,
        votes: gameState.votes,
        alivePlayers: gameState.players.filter(p => p.isAlive).length,
        myId: gameState.myId
      });
    }
  }, [phase, gameState.votes, gameState.players, gameState.myId]);

  const alivePlayers = gameState.players.filter(p => p.isAlive);
  const isDead = gameState.myId && gameState.players.find(p => p.id === gameState.myId)?.isAlive === false;
  // Hunter can act even when dead (during hunter phase)
  const isHunterPhase = phase === 'hunter' && gameState.myRole === 'hunter';
  const canAct = shouldShowAction(gameState) && (!isDead || isHunterPhase);

  const handleAction = () => {
    if (phaseInfo.action === 'werewolf' && (selectedTarget || selectedTarget === 'dont-kill')) {
      // Send 'dont-kill' string if "Không giết" was selected, otherwise send the player ID
      const targetToSend = selectedTarget === 'dont-kill' ? null : selectedTarget;
      onAction('werewolf', targetToSend);
      setSelectedTarget(null);
    } else if (phaseInfo.action === 'doctor' && selectedTarget) {
      onAction('doctor', selectedTarget);
      setSelectedTarget(null);
    } else if (phaseInfo.action === 'seer' && selectedTarget) {
      // For seer, first show the result, then allow them to end turn
      if (!seerSubmitted) {
        // Submit the action to server to get the result
        // Don't set seerSubmitted yet - wait for server response
        setIsLoading(true);
        onAction('seer', selectedTarget);
      } else {
        // End turn - send nextPhase message to advance
        // Keep seerSubmitted and selectedTarget true until phase changes to maintain UI state
        setIsLoading(true);
        // Don't clear selectedTarget here - keep it for displaying the result
        // Call a new handler to end the seer turn
        if (onAction) {
          onAction('seer-end-turn', null);
        }
      }
    } else if (phaseInfo.action === 'witch-kill') {
      onAction('witch-kill', selectedTarget || null);
      setSelectedTarget(null);
    } else if ((phaseInfo.action === 'hunter' || phaseInfo.action === 'hunter-aim') && selectedTarget) {
      // Night phase uses 'hunter-aim', day phase uses 'hunter'
      const actionType = phaseInfo.action === 'hunter-aim' ? 'hunter-aim' : 'hunter';
      onAction(actionType, selectedTarget);
      setSelectedTarget(null);
    }
  };


  let description = phaseInfo.description;
  if (phase === 'voting-accuse' || phase === 'voting-decision' || phase === 'defense') {
    // Use nightVictims array if available, otherwise fall back to nightVictim for backward compatibility
    const victims = gameState.nightVictims && gameState.nightVictims.length > 0 
      ? gameState.nightVictims 
      : (gameState.nightVictim ? [gameState.nightVictim] : []);
    
    if (victims.length > 0) {
      const victimNames = victims
        .map(id => gameState.players.find(p => p.id === id)?.name)
        .filter(Boolean);
      
      if (victimNames.length > 0) {
        if (victimNames.length === 1) {
          description = `Mọi người mở mắt ra. Đêm qua, ${victimNames[0]} đã bị giết. Hãy bỏ phiếu để treo cổ một người mà bạn nghĩ là sói!`;
        } else {
          description = `Mọi người mở mắt ra. Đêm qua, ${victimNames.join(' và ')} đã bị giết. Hãy bỏ phiếu để treo cổ một người mà bạn nghĩ là sói!`;
        }
      } else {
        description = 'Mọi người mở mắt ra. Đêm qua không ai chết. Hãy bỏ phiếu để treo cổ một người mà bạn nghĩ là sói!';
      }
    } else {
      description = 'Mọi người mở mắt ra. Đêm qua không ai chết. Hãy bỏ phiếu để treo cổ một người mà bạn nghĩ là sói!';
    }
  }

  // Get the role name for current phase
  const getCurrentActingRole = () => {
    const roleMap = {
      'werewolf': 'Ma Sói',
      'doctor': 'Bảo Vệ',
      'seer': 'Tiên Tri',
      'witch-save': 'Phù Thuỷ',
      'witch-kill': 'Phù Thuỷ',
      'hunter': 'Thợ săn',
      'voting-accuse': 'Tất Cả',
      'defense': 'Nghi Phạm',
      'voting-decision': 'Tất Cả',
    };
    return roleMap[phase] || phase;
  };

  return (
    <div className="game-phase" style={{ position: 'relative' }}>
      {/* Loading overlay - don't show for seer when they've submitted and are viewing result, or when game is finished, or when player is dead, or during voting phases, or for werewolf phase (use button spinner instead) */}
      {isLoading && !(phase === 'seer' && seerSubmitted) && gameState.gameState !== 'finished' && !isDead && phase !== 'voting-accuse' && phase !== 'voting-decision' && phase !== 'defense' && phase !== 'werewolf' && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          borderRadius: '8px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 10px'
            }}></div>
            <p style={{ color: '#667eea', fontSize: '14px', margin: 0 }}>Đang xử lý...</p>
          </div>
        </div>
      )}
      <div className="phase-header">
        <h2 className="phase-title">{phaseInfo.title}</h2>
        <p className="round-info">Vòng {gameState.round}</p>
      </div>
      
      <div className="current-acting-role">
        <p><strong>Đang hoạt động:</strong> {getCurrentActingRole()}</p>
        {!canAct && phaseInfo.action && (
          <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>
            Đang chờ {getCurrentActingRole()} thực hiện hành động...
          </p>
        )}
      </div>
      
      {/* Display voteTie message if available (for round 4+ revote) */}
      {voteTieMessage && voteTieMessage.phase === phase && voteTieMessage.displayMessage && (
        <div className="vote-tie-message" style={{
          padding: '15px',
          background: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '8px',
          margin: '15px 0',
          color: '#856404',
          fontSize: '16px',
          lineHeight: '1.6'
        }}>
          {voteTieMessage.displayMessage}
        </div>
      )}
      
      {/* Display round 4+ information message for voting-accuse phase */}
      {phase === 'voting-accuse' && gameState.round >= 4 && (!voteTieMessage || !voteTieMessage.displayMessage) && (
        <div className="round-4-info" style={{
          padding: '15px',
          background: '#e8f4f8',
          border: '2px solid #667eea',
          borderRadius: '8px',
          margin: '15px 0',
          color: '#667eea',
          fontSize: '16px',
          lineHeight: '1.6'
        }}>
          ℹ️ Từ vòng 4, cần phải có ít nhất 1 người bị treo cổ, vì vậy sẽ yêu cầu bỏ phiếu lại nếu kết quả hoà nhau, và sẽ không có vòng biểu quyết giết hay không
        </div>
      )}
      
      <p className="phase-description">{description}</p>

      {phase === 'seer' && gameState.seerResult !== undefined && seerSubmitted && (
        <div className="seer-result" style={{ 
          padding: '15px', 
          backgroundColor: '#e8f5e9', 
          borderRadius: '8px', 
          margin: '15px 0',
          border: '2px solid #4CAF50'
        }}>
          <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#2e7d32', margin: '10px 0' }}>
            🔮 Kết Quả Soi:
          </p>
          <p style={{ fontSize: '16px', color: gameState.seerResult ? '#d32f2f' : '#388e3c', margin: '10px 0' }}>
            {gameState.seerResult 
              ? `⚠️ ${gameState.players.find(p => p.id === selectedTarget)?.name || 'Người này'} là Sói!` 
              : `✓ ${gameState.players.find(p => p.id === selectedTarget)?.name || 'Người này'} không phải Sói.`}
          </p>
        </div>
      )}

      {/* Werewolf Chat - show separately for werewolf phase, not tied to canAct */}
      {gameState.phase === 'werewolf' && 
       gameState.werewolves && gameState.werewolves.length > 1 && 
       gameState.myRole === 'werewolf' && 
       onWerewolfChat && (
        <WerewolfChat
          messages={werewolfChatMessages}
          onSendMessage={onWerewolfChat}
          myName={gameState.players.find(p => p.id === gameState.myId)?.name || 'Bạn'}
        />
      )}

      {canAct && phaseInfo.action && (
        <div className="action-section">
          {phaseInfo.action === 'werewolf' && (
            <>
              <h3>Chọn nạn nhân:</h3>
              {gameState.werewolves && gameState.werewolves.length > 1 && (
                <>
                  <p style={{ color: '#666', marginBottom: '10px' }}>
                    Các sói khác: {gameState.werewolves
                      .filter(id => id !== gameState.myId)
                      .map(id => gameState.players.find(p => p.id === id)?.name)
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {/* Vote progress */}
                  {gameState.werewolfVotes && (() => {
                    const totalWerewolves = gameState.werewolves?.length || 0;
                    const votedCount = Object.keys(gameState.werewolfVotes || {}).length;
                    return (
                      <p style={{ color: '#667eea', marginBottom: '15px', fontWeight: 'bold' }}>
                        Đã bỏ phiếu: {votedCount}/{totalWerewolves} sói
                      </p>
                    );
                  })()}
                  {/* Vote display */}
                  {gameState.werewolfVotes && Object.keys(gameState.werewolfVotes).length > 0 && (
                    <div style={{ 
                      padding: '15px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px', 
                      marginBottom: '15px',
                      border: '1px solid #ddd'
                    }}>
                      <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '14px', fontWeight: 'bold' }}>Kết quả bỏ phiếu:</h4>
                      {(() => {
                        const voteCounts = {};
                        Object.entries(gameState.werewolfVotes || {}).forEach(([voterId, targetId]) => {
                          const targetKey = targetId === 'dont-kill' ? 'dont-kill' : targetId;
                          if (!voteCounts[targetKey]) {
                            voteCounts[targetKey] = { count: 0, voters: [] };
                          }
                          voteCounts[targetKey].count++;
                          const voter = gameState.players.find(p => p.id === voterId);
                          if (voter) {
                            voteCounts[targetKey].voters.push(voter.name);
                          }
                        });
                        return Object.entries(voteCounts).map(([targetId, data]) => {
                          const targetName = targetId === 'dont-kill' 
                            ? 'Không giết' 
                            : (gameState.players.find(p => p.id === targetId)?.name || targetId);
                          return (
                            <div key={targetId} style={{ marginBottom: '8px', fontSize: '13px' }}>
                              <strong>{targetName}:</strong> {data.count} phiếu 
                              {data.voters.length > 0 && (
                                <span style={{ color: '#666', marginLeft: '8px' }}>
                                  ({data.voters.join(', ')})
                                </span>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </>
              )}
              {(() => {
                const hasVoted = gameState.werewolfVotes && gameState.werewolfVotes[gameState.myId];
                return (
                  <div className="player-selection">
                    {alivePlayers
                      .filter(p => !gameState.werewolves || !gameState.werewolves.includes(p.id))
                      .map(player => {
                        const voteCount = gameState.werewolfVotes 
                          ? Object.values(gameState.werewolfVotes).filter(v => v === player.id).length 
                          : 0;
                        const playerVotedFor = gameState.werewolfVotes && gameState.werewolfVotes[gameState.myId] === player.id;
                        return (
                          <button
                            key={player.id}
                            className={`vote-item ${selectedTarget === player.id ? 'selected' : ''}`}
                            onClick={() => !hasVoted && setSelectedTarget(player.id)}
                            disabled={hasVoted}
                            style={{
                              position: 'relative',
                              cursor: hasVoted ? 'not-allowed' : 'pointer',
                              opacity: hasVoted ? 0.6 : 1
                            }}
                          >
                            {player.name}
                            {voteCount > 0 && gameState.werewolves?.length > 1 && (
                              <span className="vote-count">{voteCount} phiếu</span>
                            )}
                            {playerVotedFor && (
                              <span style={{ 
                                fontSize: '10px', 
                                color: '#4CAF50', 
                                marginTop: '5px', 
                                display: 'block',
                                fontWeight: 'bold'
                              }}>
                                (Bạn đã chọn)
                              </span>
                            )}
                          </button>
                        );
                      })}
                    {/* "Không giết" option */}
                    {gameState.allowWerewolfSkipKill && (
                      <button
                        className={`vote-item ${selectedTarget === 'dont-kill' ? 'selected' : ''}`}
                        onClick={() => !hasVoted && setSelectedTarget('dont-kill')}
                        disabled={hasVoted}
                        style={{
                          position: 'relative',
                          backgroundColor: selectedTarget === 'dont-kill' ? '#e8edff' : 'white',
                          cursor: hasVoted ? 'not-allowed' : 'pointer',
                          opacity: hasVoted ? 0.6 : 1
                        }}
                      >
                        Không giết
                        {(() => {
                          const dontKillCount = gameState.werewolfVotes 
                            ? Object.values(gameState.werewolfVotes).filter(v => v === 'dont-kill').length 
                            : 0;
                          return dontKillCount > 0 && gameState.werewolves?.length > 1 && (
                            <span className="vote-count">{dontKillCount} phiếu</span>
                          );
                        })()}
                        {gameState.werewolfVotes && gameState.werewolfVotes[gameState.myId] === 'dont-kill' && (
                          <span style={{ 
                            fontSize: '10px', 
                            color: '#4CAF50', 
                            marginTop: '5px', 
                            display: 'block',
                            fontWeight: 'bold'
                          }}>
                            (Bạn đã chọn)
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const hasVoted = gameState.werewolfVotes && gameState.werewolfVotes[gameState.myId];
                return (
                  <button
                    className="button"
                    onClick={handleAction}
                    disabled={!selectedTarget || hasVoted}
                    style={{ marginTop: '15px' }}
                  >
                    {hasVoted ? 'Đã bỏ phiếu' : 'Xác Nhận'}
                  </button>
                );
              })()}
            </>
          )}

          {phaseInfo.action === 'doctor' && (
            <>
              <h3>Chọn người để bảo vệ:</h3>
              <div className="player-selection">
                {alivePlayers.map(player => {
                  const isLastSaved = gameState.lastDoctorSave === player.id;
                  const isDisabled = isLastSaved;
                  return (
                    <button
                      key={player.id}
                      className={`vote-item ${selectedTarget === player.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => {
                        if (!isDisabled) {
                          setSelectedTarget(player.id);
                        }
                      }}
                      disabled={isDisabled}
                      style={{
                        backgroundColor: selectedTarget === player.id ? '#667eea' : (isDisabled ? '#e0e0e0' : '#f0f0f0'),
                        color: selectedTarget === player.id ? 'white' : (isDisabled ? '#999' : '#333'),
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1,
                        border: isDisabled ? '2px solid #ccc' : (selectedTarget === player.id ? '2px solid #667eea' : '1px solid #ddd')
                      }}
                      title={isDisabled ? 'Người này đã được bảo vệ ở vòng trước' : ''}
                    >
                      {player.name}
                      {isDisabled && (
                        <span style={{ marginLeft: '8px', fontSize: '12px', color: '#999' }}>
                          (Đã bảo vệ vòng trước)
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                className="button"
                onClick={handleAction}
                disabled={!selectedTarget || gameState.lastDoctorSave === selectedTarget}
                style={{ marginTop: '15px' }}
              >
                Xác Nhận
              </button>
              {gameState.lastDoctorSave && (
                <p style={{ marginTop: '10px', fontSize: '12px', color: '#ff9800', fontStyle: 'italic' }}>
                  ⚠️ Bạn không thể bảo vệ cùng một người trong hai vòng liên tiếp
                </p>
          )}
        </>
      )}

          {phaseInfo.action === 'seer' && (
            <>
              {!seerSubmitted ? (
                <>
                  <h3>Chọn người để soi:</h3>
                  {isLoading ? (
                    <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                      <div style={{
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #4CAF50',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <span style={{ color: '#4CAF50', fontSize: '14px' }}>Đang xử lý...</span>
                    </div>
                  ) : (
                    <>
                      <div className="player-selection">
                        {alivePlayers
                          .filter(p => p.id !== gameState.myId)
                          .map(player => (
                            <button
                              key={player.id}
                              className={`vote-item ${selectedTarget === player.id ? 'selected' : ''}`}
                              onClick={() => setSelectedTarget(player.id)}
                              disabled={seerSubmitted || isLoading}
                            >
                              {player.name}
                            </button>
                          ))}
                      </div>
                      <button
                        className="button"
                        onClick={handleAction}
                        disabled={!selectedTarget || isLoading}
                        style={{ marginTop: '15px' }}
                      >
                        Xác Nhận Soi
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div style={{ marginTop: '15px' }}>
                    <p style={{ color: '#666', marginBottom: '15px' }}>
                      Đã soi: <strong>{gameState.players.find(p => p.id === selectedTarget)?.name}</strong>
                    </p>
                    {isLoading ? (
                      <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                        <div style={{
                          border: '3px solid #f3f3f3',
                          borderTop: '3px solid #4CAF50',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <span style={{ color: '#4CAF50', fontSize: '14px' }}>Đang xử lý...</span>
                      </div>
                    ) : (
                      <button
                        className="button"
                        onClick={handleAction}
                        style={{ 
                          marginTop: '15px',
                          backgroundColor: '#4CAF50',
                          fontSize: '16px',
                          padding: '12px 24px'
                        }}
                      >
                        Kết Thúc Lượt
                      </button>
                    )}
                  </div>
                </>
          )}
        </>
      )}

          {phaseInfo.action === 'witch-save' && (
            <>
              {gameState.werewolfTarget && !gameState.witchUsedSave && (
                <div className="victim-info">
                  <p><strong>Nạn nhân:</strong> {gameState.players.find(p => p.id === gameState.werewolfTarget)?.name}</p>
                </div>
              )}
              {!gameState.werewolfTarget && !gameState.witchUsedSave && (
                <div style={{ padding: '15px', backgroundColor: '#e8f4f8', border: '2px solid #667eea', borderRadius: '8px', marginBottom: '15px' }}>
                  <p style={{ margin: 0, color: '#667eea', fontWeight: 'bold' }}>
                    ℹ️ Đêm nay không có nạn nhân từ ma sói.
                  </p>
                </div>
              )}
              {gameState.witchUsedSave && (
                <div style={{ padding: '15px', backgroundColor: '#fff3cd', border: '2px solid #ffc107', borderRadius: '8px', marginBottom: '15px' }}>
                  <p style={{ margin: 0, color: '#856404', fontWeight: 'bold' }}>
                    ⚠️ Bạn đã sử dụng kỹ năng cứu. Nhấn "Không Cứu" hoặc "Kết Thúc Lượt" để tiếp tục.
                  </p>
                </div>
              )}
              {isLoading ? (
                <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <div style={{
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #667eea',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span style={{ color: '#667eea', fontSize: '14px' }}>Đang xử lý...</span>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <button
                    className="button"
                    onClick={() => {
                      setIsLoading(true);
                      onAction('witch-save', null, false);
                    }}
                    style={{ flex: 1, backgroundColor: '#6c757d' }}
                  >
                    {gameState.witchUsedSave ? 'Kết Thúc Lượt' : 'Không Cứu'}
                  </button>
                  <button
                    className="button"
                    onClick={() => {
                      setIsLoading(true);
                      // Clicking "Cứu Người Này" directly saves the player
                      onAction('witch-save', null, true);
                    }}
                    disabled={gameState.witchUsedSave || !gameState.werewolfTarget}
                    style={{ flex: 1, opacity: (gameState.witchUsedSave || !gameState.werewolfTarget) ? 0.6 : 1, cursor: (gameState.witchUsedSave || !gameState.werewolfTarget) ? 'not-allowed' : 'pointer' }}
                  >
                    {gameState.witchUsedSave ? 'Đã Dùng' : 'Cứu Người Này'}
                  </button>
                </div>
          )}
        </>
      )}

          {phaseInfo.action === 'witch-kill' && (
            <>
              {gameState.witchUsedKill && (
                <div style={{ padding: '15px', backgroundColor: '#fff3cd', border: '2px solid #ffc107', borderRadius: '8px', marginBottom: '15px' }}>
                  <p style={{ margin: 0, color: '#856404', fontWeight: 'bold' }}>
                    ⚠️ Bạn đã sử dụng kỹ năng giết. Nhấn "Không Giết" hoặc "Kết Thúc Lượt" để tiếp tục.
                  </p>
                </div>
              )}
              <h3>Chọn người để giết (hoặc bỏ qua):</h3>
              <div className="player-selection">
                {alivePlayers
                  .filter(p => p.id !== gameState.myId && p.id !== gameState.nightVictim)
                  .map(player => (
                    <button
                      key={player.id}
                      className={`vote-item ${selectedTarget === player.id ? 'selected' : ''}`}
                      onClick={() => !gameState.witchUsedKill && setSelectedTarget(player.id)}
                      disabled={gameState.witchUsedKill}
                      style={{ opacity: gameState.witchUsedKill ? 0.5 : 1 }}
                    >
                      {player.name}
                    </button>
                  ))}
              </div>
              {isLoading ? (
                <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <div style={{
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #667eea',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span style={{ color: '#667eea', fontSize: '14px' }}>Đang xử lý...</span>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <button
                    className="button"
                    onClick={() => {
                      setIsLoading(true);
                      setSelectedTarget(null);
                      onAction('witch-kill', null);
                    }}
                    style={{ flex: 1, backgroundColor: '#6c757d' }}
                  >
                    {gameState.witchUsedKill ? 'Kết Thúc Lượt' : 'Không Giết'}
                  </button>
                  <button
                    className="button"
                    onClick={handleAction}
                    disabled={!selectedTarget || gameState.witchUsedKill}
                    style={{ flex: 1 }}
                  >
                    {gameState.witchUsedKill ? 'Đã Dùng' : 'Giết Người Này'}
                  </button>
                </div>
          )}
        </>
      )}

          {(phaseInfo.action === 'hunter' || phaseInfo.action === 'hunter-aim') && (
            <>
              {gameState.gameState === 'night' ? (
                <>
                  {/* Night phase: Hunter aims at target (proactive) */}
                  <h3>Chọn người để nhắm:</h3>
                  {gameState.hunterAimTarget ? (
                    <div style={{ padding: '15px', backgroundColor: '#e8f4f8', border: '2px solid #667eea', borderRadius: '8px', marginBottom: '15px' }}>
                      <p style={{ margin: 0, color: '#333', fontWeight: 'bold' }}>
                        Bạn đang nhắm vào: {gameState.players.find(p => p.id === gameState.hunterAimTarget)?.name || 'Unknown'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="player-selection">
                        {alivePlayers
                          .filter(p => p.id !== gameState.myId)
                          .map(player => (
                            <button
                              key={player.id}
                              className={`vote-item ${selectedTarget === player.id ? 'selected' : ''}`}
                              onClick={() => setSelectedTarget(player.id)}
                            >
                              {player.name}
                            </button>
                          ))}
                      </div>
                      <button
                        className="button"
                        onClick={handleAction}
                        disabled={!selectedTarget}
                        style={{ marginTop: '15px' }}
                      >
                        Xác Nhận
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* Day phase: Hunter selects target to kill (reactive death phase) */}
                  <h3>Chọn người để chết cùng:</h3>
                  {gameState.nightActions?.hunter ? (
                    <div style={{ padding: '15px', backgroundColor: '#e8f4f8', border: '2px solid #667eea', borderRadius: '8px', marginBottom: '15px' }}>
                      <p style={{ margin: 0, color: '#333', fontWeight: 'bold' }}>
                        Đã chọn: {gameState.players.find(p => p.id === gameState.nightActions.hunter)?.name || 'Unknown'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="player-selection">
                        {alivePlayers
                          .filter(p => p.id !== gameState.myId)
                          .map(player => (
                            <button
                              key={player.id}
                              className={`vote-item ${selectedTarget === player.id ? 'selected' : ''}`}
                              onClick={() => setSelectedTarget(player.id)}
                            >
                              {player.name}
                            </button>
                          ))}
                      </div>
                      <button
                        className="button"
                        onClick={handleAction}
                        disabled={!selectedTarget}
                        style={{ marginTop: '15px' }}
                      >
                        Xác Nhận
                      </button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {gameState.phase === 'voting-accuse' && (
        <div style={{ marginTop: '20px' }}>
          <h3>Chọn người chơi mà bạn nghĩ là ma sói:</h3>
          <div className="player-selection">
            {alivePlayers.length === 0 ? (
              <p style={{ color: '#e74c3c' }}>Không có người chơi nào còn sống để bỏ phiếu.</p>
            ) : (
              <>
                {alivePlayers.map(player => {
                  const isMyVote = gameState.votes && gameState.votes[gameState.myId] === player.id;
                  const isSelected = selectedVoteTarget === player.id;
                  const isMyself = player.id === gameState.myId;
                  const voteCount = gameState.votes ? Object.values(gameState.votes).filter(v => v === player.id).length : 0;
                  const isDisabled = isMyVote || isMyself;
                  return (
                    <button
                      key={player.id}
                      className={`vote-item ${isMyVote ? 'selected' : ''} ${isSelected && !isMyVote && !isMyself ? 'selected' : ''} ${isMyself ? 'disabled-self' : ''}`}
                      onClick={() => {
                        if (!isMyVote && !isMyself) {
                          console.log('Vote target selected:', player.id, player.name);
                          setSelectedVoteTarget(player.id);
                        }
                      }}
                      style={{
                        backgroundColor: isMyVote ? '#4CAF50' : (isSelected && !isMyVote && !isMyself ? '#e8f4f8' : (isMyself ? '#e0e0e0' : '#f0f0f0')),
                        color: isMyVote ? 'white' : (isMyself ? '#999' : '#333'),
                        fontWeight: (isMyVote || (isSelected && !isMyVote && !isMyself)) ? 'bold' : 'normal',
                        border: isMyVote ? '2px solid #45a049' : (isSelected && !isMyVote && !isMyself ? '2px solid #667eea' : (isMyself ? '2px solid #ccc' : '1px solid #ddd')),
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                      disabled={isDisabled}
                      title={isMyself ? 'Bạn không thể bỏ phiếu cho chính mình' : ''}
                    >
                      {player.name}
                      {isMyself && <span style={{ marginLeft: '5px', fontSize: '12px', color: '#999' }}>(Bạn)</span>}
                      {voteCount > 0 && (
                        <span className="vote-count" style={{ marginLeft: '8px', fontSize: '12px' }}>
                          ({voteCount} phiếu)
                        </span>
                      )}
                      {isMyVote && (
                        <span style={{ marginLeft: '8px', fontSize: '12px' }}>✓</span>
                      )}
                    </button>
                  );
                })}
              </>
            )}
          </div>
          
          {/* Submit vote button */}
          {!gameState.votes || !gameState.votes[gameState.myId] ? (
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                className="button"
                onClick={() => {
                  if (selectedVoteTarget) {
                    console.log('Submitting vote for:', selectedVoteTarget);
                    setIsLoading(true);
                    onVote(selectedVoteTarget);
                    setSelectedVoteTarget(null);
                  } else {
                    alert('Vui lòng chọn một người chơi trước khi bỏ phiếu');
                  }
                }}
                disabled={!selectedVoteTarget || isDead}
                style={{
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: selectedVoteTarget ? '#667eea' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedVoteTarget ? 'pointer' : 'not-allowed'
                }}
              >
                {selectedVoteTarget ? `Xác Nhận Bỏ Phiếu cho ${alivePlayers.find(p => p.id === selectedVoteTarget)?.name}` : 'Chọn người chơi để bỏ phiếu'}
              </button>
            </div>
          ) : null}
          
          {gameState.votes && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
              <p style={{ fontSize: '13px', color: '#666', margin: '5px 0' }}>
                <strong>Đã có {Object.keys(gameState.votes).length} / {alivePlayers.length} người bỏ phiếu</strong>
              </p>
              {gameState.votes[gameState.myId] ? (
                <p style={{ fontSize: '12px', color: '#28a745', margin: '5px 0' }}>
                  ✓ Bạn đã bỏ phiếu cho: {alivePlayers.find(p => p.id === gameState.votes[gameState.myId])?.name}
                </p>
              ) : (
                <p style={{ fontSize: '12px', color: '#ff9800', margin: '5px 0' }}>
                  ⚠️ Bạn chưa bỏ phiếu. Vui lòng chọn một người chơi ở trên và nhấn "Xác Nhận Bỏ Phiếu".
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {gameState.phase === 'defense' && (
        <>
          {gameState.accusedPlayer === gameState.myId ? (
            <div style={{
              padding: '30px',
              backgroundColor: '#fff3cd',
              border: '3px solid #ffc107',
              borderRadius: '12px',
              textAlign: 'center',
              marginTop: '20px'
            }}>
              <h2 style={{ color: '#856404', marginBottom: '20px' }}>
                ⚠️ Bạn đang bị nghi ngờ là ma sói
              </h2>
              <p style={{ fontSize: '18px', color: '#856404', fontWeight: 'bold', lineHeight: '1.6' }}>
                Hãy thuyết phục người chơi khác tin bạn không phải ma sói.
              </p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '15px' }}>
                Bạn có 30 giây để bảo vệ bản thân trước khi người chơi khác quyết định.
              </p>
              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                backgroundColor: '#fff', 
                borderRadius: '8px',
                border: '2px solid #ffc107'
              }}>
                <p style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold', 
                  color: defenseTimer <= 10 ? '#dc3545' : '#856404',
                  margin: 0,
                  transition: 'color 0.3s'
                }}>
                  ⏱️ {defenseTimer} giây còn lại
                </p>
              </div>
              {onSkipPhase && (
                <button
                  className="button"
                  onClick={onSkipPhase}
                  style={{
                    marginTop: '20px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    backgroundColor: '#667eea',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Kết Thúc Biện Hộ
                </button>
              )}
            </div>
          ) : (
            <div style={{
              padding: '20px',
              backgroundColor: '#f8f9fa',
              border: '2px solid #667eea',
              borderRadius: '12px',
              textAlign: 'center',
              marginTop: '20px'
            }}>
              <h3 style={{ color: '#667eea', marginBottom: '15px' }}>
                {gameState.players.find(p => p.id === gameState.accusedPlayer)?.name} đang bảo vệ bản thân
              </h3>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Hãy lắng nghe và sau đó quyết định: Giết hay đổi ý?
              </p>
            </div>
          )}
        </>
      )}

      {phaseInfo.action === 'vote' && gameState.phase === 'voting-decision' && (
        <>
          {gameState.accusedPlayer && (
            <div style={{
              padding: '15px',
              backgroundColor: '#fff3cd',
              border: '2px solid #ffc107',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#856404' }}>
                Người bị nghi ngờ: <strong>{gameState.players.find(p => p.id === gameState.accusedPlayer)?.name}</strong>
              </p>
            </div>
          )}
          {gameState.accusedPlayer === gameState.myId ? (
            <div style={{
              padding: '30px',
              backgroundColor: '#f8f9fa',
              border: '2px solid #667eea',
              borderRadius: '12px',
              textAlign: 'center',
              marginTop: '20px'
            }}>
              <h3 style={{ color: '#667eea', marginBottom: '15px' }}>
                ⏳ Chờ đợi mọi người bỏ phiếu
              </h3>
              <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.6', fontWeight: 'bold' }}>
                Chờ đợi mọi người bỏ phiếu giết hay không
              </p>
              {gameState.decisionVotes && (
                <p style={{ fontSize: '14px', color: '#999', marginTop: '15px' }}>
                  Đã có {Object.keys(gameState.decisionVotes).length} / {alivePlayers.filter(p => p.id !== gameState.accusedPlayer).length} người quyết định
                </p>
              )}
            </div>
          ) : (
            <>
              <h3>Quyết định của bạn:</h3>
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '20px' }}>
                <button
                  className="button"
                  onClick={() => {
                    if (gameState.accusedPlayer) {
                      console.log('Voting to kill:', gameState.accusedPlayer);
                      setIsLoading(true);
                      onVote('kill');
                    }
                  }}
                  disabled={isDead || (gameState.decisionVotes && gameState.decisionVotes[gameState.myId])}
                  style={{
                    padding: '15px 30px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (isDead || (gameState.decisionVotes && gameState.decisionVotes[gameState.myId])) ? 'not-allowed' : 'pointer',
                    opacity: (isDead || (gameState.decisionVotes && gameState.decisionVotes[gameState.myId])) ? 0.6 : 1,
                    flex: 1,
                    maxWidth: '200px'
                  }}
                >
                  🔪 Giết
                </button>
                <button
                  className="button"
                  onClick={() => {
                    if (gameState.accusedPlayer) {
                      console.log('Voting to change mind');
                      setIsLoading(true);
                      onVote('change-mind');
                    }
                  }}
                  disabled={isDead || (gameState.decisionVotes && gameState.decisionVotes[gameState.myId])}
                  style={{
                    padding: '15px 30px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    backgroundColor: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (isDead || (gameState.decisionVotes && gameState.decisionVotes[gameState.myId])) ? 'not-allowed' : 'pointer',
                    opacity: (isDead || (gameState.decisionVotes && gameState.decisionVotes[gameState.myId])) ? 0.6 : 1,
                    flex: 1,
                    maxWidth: '200px'
                  }}
                >
                  💭 Đổi Ý
                </button>
              </div>
              {gameState.decisionVotes && (
                <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px', textAlign: 'center' }}>
                  <p style={{ fontSize: '13px', color: '#666', margin: '5px 0' }}>
                    <strong>Đã có {Object.keys(gameState.decisionVotes).length} / {alivePlayers.filter(p => p.id !== gameState.accusedPlayer).length} người quyết định</strong>
                  </p>
                  {gameState.decisionVotes[gameState.myId] ? (
                    <p style={{ fontSize: '12px', color: '#28a745', margin: '5px 0' }}>
                      ✓ Bạn đã quyết định: <strong>{gameState.decisionVotes[gameState.myId] === 'kill' ? 'Giết' : 'Đổi Ý'}</strong>
                    </p>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#ff9800', margin: '5px 0' }}>
                      ⚠️ Bạn chưa quyết định. Vui lòng chọn "Giết" hoặc "Đổi Ý" ở trên.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {!canAct && phaseInfo.action && (
        <div className="wait-message">
          {isDead ? (
            <p style={{ color: '#8b0000', fontWeight: 'bold' }}>
              💀 Bạn đã chết. Bạn chỉ có thể quan sát trò chơi.
            </p>
          ) : (
            <p>Đang chờ người chơi khác thực hiện hành động...</p>
          )}
        </div>
      )}
    </div>
  );
}

function shouldShowAction(gameState) {
  const phase = gameState.phase;
  const myRole = gameState.myRole;

  if (phase === 'werewolf' && myRole === 'werewolf') {
    return true;
  }
  if (phase === 'doctor' && myRole === 'doctor') {
    return true;
  }
  if (phase === 'seer' && myRole === 'seer') {
    return true;
  }
  if (phase === 'witch-save' && myRole === 'witch') {
    return true;
  }
  if (phase === 'witch-kill' && myRole === 'witch') {
    return true;
  }
  if (phase === 'hunter' && myRole === 'hunter') {
    return true;
  }
  if (phase === 'voting-accuse' || phase === 'voting-decision') {
    return true;
  }
  if (phase === 'defense') {
    // Defense phase - accused player can see their defense screen, others wait
    return true; // Show UI for all players
  }
  return false;
}

export default GamePhase;
